import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Send,
  Mic,
  Globe,
  Volume2,
  ArrowLeft,
  Loader2,
  Sparkles,
  HelpCircle,
  StopCircle,
  X,
  Camera,
  FileQuestion,
  BookMarked,
  CheckCircle,
  ChevronRight,
  Trash2,
  Eye,
  Save,
  RefreshCw,
  ClipboardList,
  Download,
  BookOpen,
  FileText,
  Users,
  Mail,
  GraduationCap,
  UserCheck,
  Lightbulb,
  PanelLeft,
  Ruler,
  Pencil,
  Bookmark,
  MessageSquare,
  GitBranch,
  MessageCircle,
  NotebookPen,
  BarChart3,
  Scale,
  Layers,
  History,
  Share2,
  Grid3X3,
  Clock,
  ArrowUpRight,
  Square,
  Play,
  Pause
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Textarea } from '@/components/ui/textarea';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { geminiMultimodalGenerate } from '@/services/geminiServices';
import { generateQuestions } from '@/services/root_utils/geminiServices';
import { db, auth } from '@/firebase/config';
import { collection, doc, setDoc, getDocs, deleteDoc, serverTimestamp, query as firestoreQuery, orderBy } from 'firebase/firestore';
import WorkspaceLoader from './WorkspaceLoader';
import jsPDF from 'jspdf';
import html2pdf from 'html2pdf.js';
import { marked } from 'marked';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// --- System Prompt ---
const SYSTEM_PROMPT = `
You are an AI-powered question-answering assistant designed as a minimum viable product (MVP) for educational and informational queries. Your core features are:

1. **Text Input Handling**: When the user types in a question, analyze it and provide a clear, well-organized response.
2. **Image Input Handling**: If the user uploads or describes an image of a question (e.g., via a photo of a textbook page, worksheet, or handwritten note), simulate scanning it by interpreting the described content and treating it as the query.
3. **Multi-Question Sessions**: Allow users to ask follow-up questions in a conversation thread. The AI remembers previous context (e.g., "Explain step 2 in more detail") to provide coherent, evolving responses. This turns it into a tutoring tool without needing full chat history storage initially.
4. **Clarification Prompts**: If a question is ambiguous (e.g., vague math problem), the AI automatically asks for more details before answering, reducing errors and improving accuracy.
5. **Voice Input/Output**: Integrate basic speech-to-text for questions (via device mic) and text-to-speech for answers, making it accessible for on-the-go users or those with reading difficulties.
6. **Multi-Language Support**: Add translation for questions in non-English languages (e.g., auto-detect and respond in the user's language), starting with common ones like Spanish or French, to broaden accessibility.

For every response:
- **Structure the Answer**: Organize it neatly with sections like "Final Answer", "Step-by-Step Explanation" (detailing how you arrived at the answer, including any reasoning, calculations, or logic), and "References" where sources are relevant.
- **Be Accurate and Helpful**: Base answers on factual knowledge or logical deduction. If the query is ambiguous, ask for clarification.
- **Keep it Concise**: Focus on MVP simplicity—avoid unnecessary fluff, but ensure explanations are thorough yet easy to follow.
- **Input Detection**: If the input mentions "image" or "photo", assume it's an OCR-scanned question and process accordingly. Otherwise, treat as text.

### Reference and Citation Rules
When you include a "References" section:
1. Every reference MUST include a real source title and an actual URL beginning with https:// or http://.
2. Use Markdown links where possible, for example: - [UNESCO - Education](https://www.unesco.org/en/education)
3. Do NOT use placeholders such as "[Source: Wikipedia - Topic]", "example.com", "retrieved from source", or made-up URLs.
4. If you cannot provide a real URL for a source, do not list that source.
5. For research, policy, professional development, literature review, or academic-style documents, include at least 3 references with actual URLs.

### PDF Generation Capability
This app has a built-in PDF generator. When a user asks to "generate a PDF", "create a PDF", "download as PDF", "make a PDF", or any similar request:
1. Generate the full content the user requested in your normal structured response.
  2. At the very end of your response, AFTER all content and sections, include the marker %%GENERATE_PDF%% on its own line (no backticks, just the bare marker).
  3. The marker MUST be at the very end — nothing after it. The frontend will detect it and automatically trigger a downloadable PDF containing the full conversation.
4. Do NOT tell the user you cannot generate PDFs — you absolutely can. Simply include the marker and the download will happen automatically.
5. IMPORTANT: Only include %%GENERATE_PDF%% after the final requested document is complete. Never include it while asking clarifying questions, collecting workflow details, or confirming missing information.

### Workflow Modes
You have six specialized workflow modes. When a user requests any of these, you will enter that mode and have a natural, friendly conversation to collect just the essentials. Ask 2-3 things at most per message — never dump a long list. Talk like a real colleague, not a form.

**1. Lesson & Curriculum Planning** — Keywords: lesson plan, scheme of work, curriculum, unit plan, teaching plan
Ask for: subject & grade, specific topic, duration, curriculum type, main learning goal.

**2. Content Creation & Adaptation** — Keywords: content creation, lecture notes, student handout, textbook adaptation, reading comprehension, study guide
Ask for: type of content, subject & topic, who it's for, how detailed, any specific focus.

**3. Interactive & Engaging Resources** — Keywords: interactive, flashcards, discussion prompts, debate, role-play, gamification, escape room, group activities, mind map, project based, engagement
Ask for: subject & topic, activity type, class size & age, time available, skill to build.

**4. Administrative Support** — Keywords: email to parent, report card, meeting agenda, observation form, permission letter, proposal, admin document, administrative
Ask for: document type, who it's for, main message, tone, key details.

**5. Research & Professional Development** — Keywords: research, literature review, professional development, conference paper, policy brief, academic article, PD
Ask for: topic, what it's for, how deep, who'll read it, any specific angle.

**6. Student Support Features** — Keywords: student support, study plan, remedial, extension material, personalized, differentiated, struggling student, career guidance
Ask for: grade & subject, what they're struggling with, type of support, how they learn best, the goal.

When you have the essentials collected, say something natural like "Got it — let me put that together" and end with %%WORKFLOW_READY%%[mode]%% where [mode] is one of: lesson, content, interactive, admin, research, student.
Then generate the full output. The frontend will detect this marker and create a downloadable PDF card.
`;

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isImage?: boolean;
  isAudio?: boolean;
  audioUrl?: string;
  quizCard?: GeneratedQuizCard; // attached quiz card payload
  pdfCard?: GeneratedPDFCard;   // attached PDF download card payload
  workflowCard?: WorkflowCard;  // attached workflow output card
}

interface QuizBuilderParams {
  title: string;
  subject: string;
  topics: string[];
  numQuestions: number;
  questionType: string; // 'MCQ' | 'Short Answer' | 'Essay' | 'Mixed'
  difficulty: 'Easy' | 'Medium' | 'Hard';
  totalMarks: number;
}

interface GeneratedQuizCard {
  params: QuizBuilderParams;
  questions: any[];
}

interface GeneratedPDFCard {
  title: string;
  blobUrl: string;
  fileName: string;
  pageCount: number;
}

interface SavedQuiz {
  id: string;
  title: string;
  type: string;
  totalMarks: number;
  questions: any[];
  createdAt: any;
  status: string;
}

type WorkflowMode = 'lesson' | 'content' | 'interactive' | 'admin' | 'research' | 'student' | 'rubric' | 'homework' | 'reference' | 'parentEmail' | 'differentiation' | 'feedback' | 'reflection' | 'analytics' | 'standards';

const WORKFLOW_DISPLAY_NAMES: Record<WorkflowMode, string> = {
  lesson: 'Lesson Plan',
  content: 'Content',
  interactive: 'Interactive Resource',
  admin: 'Admin Document',
  research: 'Research',
  student: 'Student Support',
  rubric: 'Rubric',
  homework: 'Worksheet',
  reference: 'Reference Card',
  parentEmail: 'Parent Email',
  differentiation: 'Differentiation Matrix',
  feedback: 'Feedback',
  reflection: 'Lesson Reflection',
  analytics: 'Assessment Analytics',
  standards: 'Standards Alignment'
};

const WORKFLOW_ACTIVE_NAMES: Record<WorkflowMode, string> = {
  lesson: 'Lesson Planner',
  content: 'Content Creator',
  interactive: 'Interactive Resources',
  admin: 'Admin Support',
  research: 'Research & PD',
  student: 'Student Support',
  rubric: 'Rubric Generator',
  homework: 'Worksheet Builder',
  reference: 'Reference Cards',
  parentEmail: 'Parent Email Writer',
  differentiation: 'Differentiation Matrix',
  feedback: 'Student Feedback',
  reflection: 'Lesson Reflection',
  analytics: 'Assessment Analytics',
  standards: 'Standards Alignment'
};

interface WorkflowConfig {
  id: WorkflowMode;
  label: string;
  icon: any;
  color: string;
  bg: string;
  border: string;
  keywords: RegExp;
  prompt: string;
}

interface LessonPlanParams {
  subject: string;
  grade: string;
  topic: string;
  subTopics: string[];
  duration: string;
  curriculum: string;
  objectives: string;
  competencies: string;
  priorKnowledge: string;
  teachingMethods: string;
  resources: string;
  differentiation: string;
  assessment: string;
  localContext: string;
  tone: string;
}

interface ContentCreationParams {
  contentType: string;
  subject: string;
  topic: string;
  level: string;
  sourceMaterial: string;
  length: string;
  adaptation: string;
  formatting: string;
  emphasis: string;
  languageLevel: string;
}

interface InteractiveResourceParams {
  subject: string;
  topic: string;
  resourceType: string;
  classSize: string;
  ageGrade: string;
  objectives: string;
  timeAvailable: string;
  skillFocus: string;
  constraints: string;
  engagementLevel: string;
  format: string;
}

interface AdminDocParams {
  docType: string;
  purpose: string;
  audience: string;
  keyPoints: string;
  tone: string;
  details: string;
  senderName: string;
  position: string;
  schoolName: string;
  deadlines: string;
  length: string;
}

interface ResearchParams {
  topic: string;
  purpose: string;
  depth: string;
  audience: string;
  sources: string;
  timePeriod: string;
  format: string;
  localContext: string;
}

interface StudentSupportParams {
  grade: string;
  subject: string;
  topic: string;
  supportType: string;
  performanceLevel: string;
  learningStyle: string;
  timeAvailable: string;
  goals: string;
  specialNeeds: string;
  parentCommunication: string;
}

interface WorkflowCard {
  mode: WorkflowMode;
  title: string;
  params: any;
  content: string;
  blobUrl?: string;
  fileName?: string;
}

// Render inline math with KaTeX
const renderInlineMath = (formula: string) => {
  try {
    return { __html: katex.renderToString(formula, { throwOnError: false }) };
  } catch {
    return null;
  }
};

// Render display math with KaTeX
const renderDisplayMath = (formula: string) => {
  try {
    return { __html: katex.renderToString(formula, { throwOnError: false, displayMode: true }) };
  } catch {
    return null;
  }
};

// Helper to format text with bold, links, and math
const formatText = (text: string) => {
  // Regex to match math, links, URLs, and bold text
  // 1. Display math $$...$$
  // 2. Display math \[...\]
  // 3. Inline math $...$
  // 4. Inline math \(...\)
  // 5. Markdown Links: [text](url)
  // 6. Raw URLs: http://... or https://...
  // 7. Bold: **text**
  const regex = /(\$\$[\s\S]*?\$\$)|(\\\[[\s\S]*?\\\])|(\$[^$\n]*?\$)|(\\\(.*?\\\))|(\[.*?\]\(https?:\/\/[^\s)]+\))|(https?:\/\/[^\s]+)|(\*\*.*?\*\*)/g;
  
  const parts = text.split(regex);
  
  return parts.map((part, index) => {
    if (!part) return null;

    // Handle Display Math $$...$$
    if (part.startsWith('$$') && part.endsWith('$$')) {
      const formula = part.slice(2, -2);
      const html = renderDisplayMath(formula);
      if (html) {
        return <div key={index} className="my-2 flex justify-center overflow-x-auto py-1" dangerouslySetInnerHTML={html} />;
      }
      return <span key={index} className="text-red-500 text-xs">[Math Error]</span>;
    }

    // Handle Display Math \[...\]
    if (part.startsWith('\\[') && part.endsWith('\\]')) {
      const formula = part.slice(2, -2);
      const html = renderDisplayMath(formula);
      if (html) {
        return <div key={index} className="my-2 flex justify-center overflow-x-auto py-1" dangerouslySetInnerHTML={html} />;
      }
      return <span key={index} className="text-red-500 text-xs">[Math Error]</span>;
    }

    // Handle Inline Math $...$
    if (part.startsWith('$') && part.endsWith('$')) {
      const formula = part.slice(1, -1);
      const html = renderInlineMath(formula);
      if (html) {
        return <span key={index} dangerouslySetInnerHTML={html} />;
      }
      return <span key={index}>{part}</span>;
    }

    // Handle Inline Math \(...\)
    if (part.startsWith('\\(') && part.endsWith('\\)')) {
      const formula = part.slice(2, -2);
      const html = renderInlineMath(formula);
      if (html) {
        return <span key={index} dangerouslySetInnerHTML={html} />;
      }
      return <span key={index}>{part}</span>;
    }

    // Handle Markdown Link: [text](url)
    if (part.startsWith('[') && part.includes('](') && part.endsWith(')')) {
      const match = part.match(/\[(.*?)\]\((.*?)\)/);
      if (match) {
        return (
          <a 
            key={index} 
            href={match[2]} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-blue-600 hover:underline break-words"
          >
            {match[1]}
          </a>
        );
      }
    }

    // Handle Raw URL
    if (part.startsWith('http://') || part.startsWith('https://')) {
      return (
        <a 
          key={index} 
          href={part} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-blue-600 hover:underline break-words"
        >
          {part}
        </a>
      );
    }

    // Handle Bold
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index} className="font-semibold text-gray-900 dark:text-white">{part.slice(2, -2)}</strong>;
    }

    // Plain text
    return part;
  });
};

// Split section body by display math blocks so multi-line $$...$$ renders correctly
const splitBodyByDisplayMath = (body: string) => {
  const parts = body.split(/(\$\$[\s\S]*?\$\$)|(\\\[[\s\S]*?\\\])/g);
  return parts.map((part, idx) => {
    if (!part) return null;
    if (part.startsWith('$$') && part.endsWith('$$')) {
      const formula = part.slice(2, -2).trim();
      const html = renderDisplayMath(formula);
      if (html) {
        return <div key={idx} className="my-3 flex justify-center overflow-x-auto py-2" dangerouslySetInnerHTML={html} />;
      }
      return <span key={idx} className="text-red-500 text-xs">[Math Error]</span>;
    }
    if (part.startsWith('\\[') && part.endsWith('\\]')) {
      const formula = part.slice(2, -2).trim();
      const html = renderDisplayMath(formula);
      if (html) {
        return <div key={idx} className="my-3 flex justify-center overflow-x-auto py-2" dangerouslySetInnerHTML={html} />;
      }
      return <span key={idx} className="text-red-500 text-xs">[Math Error]</span>;
    }
    return part.split('\n').map((line, i) => (
      <p key={`${idx}-${i}`} className={i > 0 || idx > 0 ? "mt-2" : ""}>
        {formatText(line)}
      </p>
    ));
  });
};

const MessageContent = ({ content, role }: { content: string, role: 'user' | 'assistant' }) => {
  if (role === 'user') {
    return <p className="text-sm leading-relaxed">{formatText(content)}</p>;
  }

  // Robust splitting: checks for markdown headers (##), bold headers (**Headline**), or plain text headers
  const sections = content.split(/(?=^#{1,6}\s|^\*\*.*?\*\*|^References:?|^Final Answer:?|^Step-by-Step Explanation:?)/gmi);
  
  return (
    <div className="space-y-4">
      {sections.map((section, idx) => {
        // Clean up leading newlines
        const trimmed = section.trim(); 
        if (!trimmed) return null;

        // Check for headers (starts with # or keywords case-insensitive)
        const lowerTrimmed = trimmed.toLowerCase();
        const isHeaderBlock = /^#{1,6}\s/.test(trimmed) || 
                              lowerTrimmed.startsWith('final answer') || 
                              lowerTrimmed.startsWith('step-by-step explanation') || 
                              lowerTrimmed.startsWith('references');

        if (isHeaderBlock || /^\*\*.*?\*\*[:]/.test(trimmed) || /^\*\*.*?\*\*/.test(trimmed)) {
          const lines = trimmed.split('\n');
          // Clean header: remove #, **, and trailing colon
          const headerRaw = lines[0];
          const header = headerRaw.replace(/^#{1,6}\s+/, '').replace(/^\*\*/, '').replace(/\*\*$/, '').replace(/:$/, '').trim();
          const body = lines.slice(1).join('\n').trim();
          
          let headerColor = "text-gray-900 dark:text-gray-100";
          let bodyStyle = "text-gray-600 dark:text-gray-300";
          let containerStyle = "";

          if (header.toLowerCase().includes("final answer")) {
            headerColor = "text-emerald-600 dark:text-emerald-400";
            containerStyle = "bg-emerald-50/50 dark:bg-emerald-900/10 p-3 rounded-xl border border-emerald-100 dark:border-emerald-500/20";
            bodyStyle = "text-gray-800 dark:text-gray-200 font-medium text-lg";
          } else if (header.toLowerCase().includes("references")) {
             headerColor = "text-blue-600 dark:text-blue-400";
             containerStyle = "bg-blue-50/50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-500/20 text-sm font-medium";
          }

          return (
            <div key={idx} className={containerStyle}>
              {header && <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${headerColor}`}>{header}</h3>}
              <div className={`text-sm leading-relaxed ${bodyStyle}`}>
                 {splitBodyByDisplayMath(body)}
              </div>
            </div>
          );
        }

        // Fallback for unlabeled text (intro text)
        return (
          <div key={idx} className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
             {splitBodyByDisplayMath(trimmed)}
          </div>
        );
      })}
    </div>
  );
};

const AudioMessageBubble = ({ audioUrl }: { audioUrl?: string }) => {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.addEventListener('ended', () => { setPlaying(false); setCurrentTime(0); });
      audioRef.current.addEventListener('timeupdate', () => setCurrentTime(audioRef.current!.currentTime));
      audioRef.current.addEventListener('loadedmetadata', () => setDuration(audioRef.current!.duration));
    }
    return () => { audioRef.current?.pause(); audioRef.current = null; };
  }, [audioUrl]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2.5 min-w-[200px]">
      <button onClick={togglePlay} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center shrink-0 transition-colors">
        {playing ? <Pause className="w-3.5 h-3.5 fill-current" /> : <Play className="w-3.5 h-3.5 fill-current ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="h-1 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[9px] text-white/60 mt-1 font-mono">{fmt(currentTime)} / {duration > 0 ? fmt(duration) : '0:00'}</p>
      </div>
    </div>
  );
};

const IndependentSearchUtility = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const quickSearchReturn = (location.state as any)?.quickSearchReturn as { path: string; state?: any } | undefined;
  const quickSearchImageDataUrl = (location.state as any)?.quickSearchImageDataUrl as string | undefined;
  const quickSearchPrompt = (location.state as any)?.quickSearchPrompt as string | undefined;
  const quickSearchSourceLabel = (location.state as any)?.quickSearchSourceLabel as string | undefined;
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [loadingDetails, setLoadingDetails] = useState(true);

  // Image upload state
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  // ── Quiz builder state ──
  const [quizMode, setQuizMode] = useState(false);
  const [quizParams, setQuizParams] = useState<Partial<QuizBuilderParams>>({});
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [savingQuiz, setSavingQuiz] = useState(false);
  const [savedFilesOpen, setSavedFilesOpen] = useState(false);
  const [savedQuizzes, setSavedQuizzes] = useState<SavedQuiz[]>([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [pendingQuizCard, setPendingQuizCard] = useState<GeneratedQuizCard | null>(null);

  // ── Workflow state ──
  const [workflowMode, setWorkflowMode] = useState<WorkflowMode | null>(null);
  const [workflowParams, setWorkflowParams] = useState<Record<string, any>>({});
  const [generatingWorkflow, setGeneratingWorkflow] = useState(false);
  const [workflowPanelOpen, setWorkflowPanelOpen] = useState(false);
  const [templateLibraryOpen, setTemplateLibraryOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [resourceSharingOpen, setResourceSharingOpen] = useState(false);
  const [savedVersions, setSavedVersions] = useState<{ id: string; title: string; mode: string; content: string; date: Date; blobUrl?: string }[]>([]);

  // --- Voice Input Hooks ---
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);

  // --- Text-to-Speech Hooks ---
  const [speakingId, setSpeakingId] = useState<string | null>(null);

  // --- Image Upload Hooks ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const consumedQuickSearchRef = useRef(false);

  useEffect(() => {
    // Simulate loading details to ensure smooth transition
    const timer = setTimeout(() => {
      setLoadingDetails(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (consumedQuickSearchRef.current || !quickSearchImageDataUrl) return;
    consumedQuickSearchRef.current = true;

    const loadQuickSearchCapture = async () => {
      try {
        const response = await fetch(quickSearchImageDataUrl);
        const blob = await response.blob();
        const prompt = quickSearchPrompt || 'Analyze this cropped question image and explain the answer clearly.';
        setImageBlob(blob);
        setImagePreviewUrl(quickSearchImageDataUrl);
        setQuery(prompt);
        window.setTimeout(() => {
          handleSearch(prompt, undefined, blob);
        }, 80);
      } catch (error) {
        console.error('Failed to load quick search capture:', error);
      }
    };

    loadQuickSearchCapture();
  }, [quickSearchImageDataUrl, quickSearchPrompt]);

  if (loadingDetails) {
    return <WorkspaceLoader />;
  }

  // ── QUIZ DETECTION: detect quiz intent in user text ──
  const QUIZ_KEYWORDS = /\b(quiz|quizzes|test|exam|assessment|questions?|mcq|multiple[- ]choice|worksheet)\b/i;

  // The special AI prompt for quiz-building mode
  const QUIZ_BUILDER_SYSTEM_PROMPT = `You are a friendly educational assistant helping a teacher build a quiz/assessment.
Your job is to have a natural conversation to collect these parameters:
1. Subject (e.g. Mathematics, Biology)
2. Topics (e.g. Algebra, Cell Division)
3. Number of questions
4. Question type: MCQ, Short Answer, Essay, or Mixed
5. Difficulty: Easy, Medium, or Hard
6. Total marks

As you collect info, confirm back what you have and ask for what's missing.
When you have ALL 6 parameters confirmed by the teacher, end your message with this EXACT marker (no spaces inside %%):
%%QUIZ_PARAMS%%{"title":"...","subject":"...","topics":["..."],"numQuestions":N,"questionType":"...","difficulty":"...","totalMarks":N}%%

If the teacher says "generate", "create", "go ahead", "yes", "do it", "build it" and you have all params, also add:
%%QUIZ_READY%%

Only output ONE of these markers per message, at the very end.
Never show the raw JSON markers in your conversational text.`;

  // ── WORKFLOW MODES ──
  // Each prompt instructs the AI to be conversational and ask for only 5 essentials
  const WORKFLOW_PROMPTS: Record<WorkflowMode, string> = {
    lesson: `You are a friendly, experienced teacher helping a colleague plan a lesson. Talk like a real person — warm, casual, and helpful. NOT a robot filling out a form.

When a teacher asks for a lesson plan, have a natural back-and-forth conversation. Ask for these 5 things, but spread them out naturally — don't dump them all at once. Ask 2-3 at most per message:

1. What subject and which class/grade?
2. What specific topic are you covering? (be specific — "calculus" is too broad)
3. How long is the lesson?
4. What curriculum are you following? (CBC, 8-4-4, IB, etc.)
5. What's the main thing you want students to walk away knowing or being able to do?

If they've already told you some of this, acknowledge it and just ask what's missing. Keep it short. Once you have all 5, say something like "Got it — let me put that together for you" and then end your message with:
%%WORKFLOW_READY%%lesson%%

After the marker, generate the full lesson plan with objectives, activities, timing, differentiation tips, and a materials list. Make it practical and ready to use tomorrow.`,

    content: `You are a helpful colleague who's great at creating teaching materials. Talk like a real person — warm and practical, not like a form.

When a teacher needs content, ask for these 5 things naturally over a couple of messages:

1. What kind of thing are you making — lecture notes, a handout, a summary, something else?
2. What subject and topic?
3. Who's it for — what grade or level?
4. How detailed do you want it — a quick one-pager or something more thorough?
5. Anything specific you want me to focus on or avoid?

Acknowledge what they've already shared. Keep it conversational. Once you have all 5, say "Alright, I'll put that together" and end with:
%%WORKFLOW_READY%%content%%

Then generate the content — professionally formatted, ready to print or share.`,

    interactive: `You are a creative teacher who loves making engaging classroom activities. Talk like a real person — enthusiastic and practical.

When a teacher wants interactive resources, ask for these 5 things naturally:

1. What subject and topic?
2. What kind of activity are you thinking — flashcards, discussion, debate, group work, something else?
3. Roughly how many students and what age?
4. How much time do you have for this?
5. What skill are you trying to build — critical thinking, teamwork, recall, creativity?

Keep it light and conversational. Don't overwhelm them. Once you have all 5, say "Sounds great — let me set that up" and end with:
%%WORKFLOW_READY%%interactive%%

Then generate the full resource pack with clear instructions, student sheets, and a teacher guide.`,

    admin: `You are a helpful admin-savvy colleague who's good at writing professional documents. Talk like a real person — efficient and friendly.

When a teacher needs an admin document, ask for these 5 things naturally:

1. What document do you need — email, report comment, agenda, letter, proposal?
2. Who's it going to?
3. What's the main message or point you want to get across?
4. What tone — formal, friendly, professional, firm?
5. Any specific details I should include — names, dates, class info?

Keep it brief and natural. Once you have all 5, say "I'll draft that up for you" and end with:
%%WORKFLOW_READY%%admin%%

Then generate a polished, ready-to-use document.`,

    research: `You are a research-savvy colleague who's good at summarising and synthesising educational research. Talk like a real person — knowledgeable but approachable.

When a teacher needs research or PD material, ask for these 5 things naturally:

1. What topic or area are you looking into?
2. What do you need this for — personal reading, a presentation, a paper, a policy brief?
3. How deep do you want to go — a quick overview or something more detailed?
4. Who's going to read this — you, other teachers, school leadership?
5. Any particular angle or local context you want me to focus on?

Keep it conversational. Once you have all 5, say "I'll pull that together for you" and end with:
%%WORKFLOW_READY%%research%%

Then generate a well-researched, properly referenced document. The final document MUST include a "References" section with at least 3 real source titles and actual clickable URLs. Do not include placeholder citations or sources without links.`,

    student: `You are a caring, experienced teacher who's great at supporting individual students. Talk like a real person — warm, empathetic, and practical.

When a teacher needs student support materials, ask for these 5 things naturally:

1. What grade and subject is the student in?
2. What are they struggling with — or excelling at?
3. What kind of support do they need — study plan, extra practice, extension work, something else?
4. How do they learn best — visual, hands-on, reading, listening?
5. What's the goal — catch up, prep for a test, go deeper?

Keep it warm and conversational. Once you have all 5, say "I'll put together something tailored for them" and end with:
%%WORKFLOW_READY%%student%%

Then generate personalised resources, study plans, or support materials.`,

    rubric: `You are a teacher who's excellent at creating clear, fair grading rubrics. Talk like a real colleague — practical and helpful.

When a teacher needs a rubric, ask for these 5 things naturally:

1. What assignment or task is this rubric for?
2. What subject and grade level?
3. How many performance levels do you want — 3, 4, or 5?
4. What are the main criteria you want to assess — content, presentation, effort, creativity, accuracy?
5. Any specific standards or learning outcomes this needs to align with?

Keep it brief and conversational. Once you have all 5, say "I'll build that rubric for you" and end with:
%%WORKFLOW_READY%%rubric%%

Then generate a complete grading rubric with clear criteria, performance levels, and descriptors.`,

    homework: `You are a teacher who's great at creating meaningful homework that actually helps students learn. Talk like a real person — practical and thoughtful.

When a teacher needs homework, ask for these 5 things naturally:

1. What subject and topic is this homework for?
2. What grade level are the students?
3. How much time should it take — 15 min, 30 min, an hour?
4. What type of work — practice problems, reading, writing, research, creative task?
5. Do you want an answer key included?

Keep it light and natural. Once you have all 5, say "I'll put that together" and end with:
%%WORKFLOW_READY%%homework%%

Then generate the homework assignment with clear instructions and an answer key if requested.`,

    reference: `You are a teacher who's great at making quick reference materials that students actually use. Talk like a real colleague — clear and practical.

When a teacher needs a reference card, ask for these 5 things naturally:

1. What subject and topic is this for?
2. What grade level?
3. What should it cover — formulas, vocabulary, key concepts, steps, definitions?
4. How detailed — a one-page cheat sheet or something more thorough?
5. Any specific examples or diagrams you want included?

Keep it brief. Once you have all 5, say "I'll make that reference card" and end with:
%%WORKFLOW_READY%%reference%%

Then generate a clean, student-friendly quick reference card.`,

    parentEmail: `You are a teacher who's good at communicating with parents professionally and warmly. Talk like a real colleague.

When a teacher needs a parent email template, ask for these 5 things naturally:

1. What's the situation — progress update, concern, celebration, request for support?
2. What grade and which student or class?
3. What's the main message you want to get across?
4. What tone — warm and encouraging, formal, firm but respectful?
5. Any specific details — dates, upcoming events, action items?

Keep it natural. Once you have all 5, say "I'll draft that for you" and end with:
%%WORKFLOW_READY%%parentEmail%%

Then generate a polished, ready-to-send email template.`,

    differentiation: `You are an experienced teacher who's great at adapting lessons for mixed-ability classes. Talk like a real colleague — practical and supportive.

When a teacher needs differentiation help, ask for these 5 things naturally:

1. What's the lesson or activity you want to differentiate?
2. What subject and topic?
3. What's the range of abilities in your class — what can the strongest do, what do struggling learners need?
4. How many versions do you want — support, core, extension?
5. Any specific adjustments you already have in mind?

Keep it conversational. Once you have all 5, say "I'll create those differentiated versions" and end with:
%%WORKFLOW_READY%%differentiation%%

Then generate 3 versions of the lesson — support, core, and extension — with clear guidance for each.`,

    feedback: `You are a teacher who gives constructive, encouraging feedback that actually helps students improve. Talk like a real colleague.

When a teacher needs feedback on student work, ask for these 5 things naturally:

1. What kind of work is it — essay, math problem, project, presentation?
2. What subject and topic?
3. What grade level is the student?
4. What are the main strengths you noticed?
5. What areas need improvement?

Keep it natural and warm. Once you have all 5, say "I'll write up the feedback" and end with:
%%WORKFLOW_READY%%feedback%%

Then generate constructive, specific feedback with praise and actionable next steps.`,

    reflection: `You are a reflective teacher who helps colleagues think through their lessons and grow. Talk like a real person — thoughtful and supportive.

When a teacher wants to reflect on a lesson, ask for these 5 things naturally:

1. What lesson did you just teach — subject and topic?
2. How did it go overall — what worked well?
3. What didn't go as planned?
4. How did the students respond — engaged, confused, excited?
5. What would you do differently next time?

Keep it warm and conversational. Once you have all 5, say "Let me put together a reflection" and end with:
%%WORKFLOW_READY%%reflection%%

Then generate a structured reflection with strengths, areas for growth, and concrete suggestions for next time.`,

    analytics: `You are a data-savvy teacher who helps colleagues make sense of assessment results. Talk like a real colleague — analytical but approachable.

When a teacher needs assessment analytics, ask for these 5 things naturally:

1. What assessment was given — quiz, test, project?
2. What subject and topic?
3. How many students took it?
4. What were the overall results — average score, pass rate?
5. Any patterns you noticed — questions most got wrong, common mistakes?

Keep it natural. Once you have all 5, say "I'll analyse those results for you" and end with:
%%WORKFLOW_READY%%analytics%%

Then generate an analysis with key findings, common misconceptions, and reteaching recommendations.`,

    standards: `You are a curriculum expert who helps teachers align their work to standards. Talk like a real colleague — knowledgeable and practical.

When a teacher needs standards alignment, ask for these 5 things naturally:

1. What lesson, unit, or assessment do you want to align?
2. What curriculum framework — CBC, IB, A-Level, Common Core?
3. What subject and grade?
4. What topics or units are covered?
5. Do you want a full mapping or just the key standards?

Keep it brief. Once you have all 5, say "I'll map those standards for you" and end with:
%%WORKFLOW_READY%%standards%%

Then generate a clear standards alignment mapping with specific codes and descriptions.`
  };

  const WORKFLOW_KEYWORDS: Record<WorkflowMode, RegExp> = {
    lesson: /\b(lesson plan|scheme of work|curriculum|unit plan|teaching plan|lesson planning)\b/i,
    content: /\b(content creation|lecture notes|student handout|handout|textbook adaptation|reading comprehension|study guide|summary notes)\b/i,
    interactive: /\b(interactive|flashcards|discussion prompts|debate|role.?play|gamification|escape room|group activit|mind map|project based|engagement)\b/i,
    admin: /\b(email to parent|report card|meeting agenda|observation form|permission letter|proposal|admin document|administrative)\b/i,
    research: /\b(research|literature review|professional development|conference paper|policy brief|academic article|PD)\b/i,
    student: /\b(student support|study plan|remedial|extension material|personalized|differentiated|struggling student|career guidance)\b/i,
    rubric: /\b(rubric|grading rubric|assessment rubric|scoring guide|marking scheme|grading criteria)\b/i,
    homework: /\b(homework|assignment|practice problems|take.?home|homework assignment|set homework)\b/i,
    reference: /\b(reference card|cheat sheet|quick reference|formula sheet|key concepts|vocabulary list|study card)\b/i,
    parentEmail: /\b(parent email|email to parent|parent communication|parent letter|parent message|parent note)\b/i,
    differentiation: /\b(differentiation|differentiated|mixed ability|tiered|support and extension|three versions|adapt for)\b/i,
    feedback: /\b(student feedback|mark student|grade student|feedback on work|constructive feedback|student assessment feedback)\b/i,
    reflection: /\b(lesson reflection|reflect on lesson|how did it go|teaching reflection|post.?lesson|after teaching)\b/i,
    analytics: /\b(assessment analytics|quiz results|test analysis|exam analysis|common misconceptions|result analysis|score analysis)\b/i,
    standards: /\b(standards alignment|align to standards|curriculum standards|map standards|CBC standards|learning outcomes)\b/i
  };

  // Parse quiz marker from AI response
  function parseQuizMarkers(text: string): { clean: string; params?: Partial<QuizBuilderParams>; ready: boolean } {
    let clean = text;
    let params: Partial<QuizBuilderParams> | undefined;
    let ready = false;

    const paramsMatch = text.match(/%%QUIZ_PARAMS%%({[\s\S]*?})%%/);
    if (paramsMatch) {
      try {
        params = JSON.parse(paramsMatch[1]);
      } catch {}
      clean = clean.replace(/%%QUIZ_PARAMS%%[\s\S]*?%%/, '').trim();
    }

    if (text.includes('%%QUIZ_READY%%')) {
      ready = true;
      clean = clean.replace('%%QUIZ_READY%%', '').trim();
    }

    return { clean, params, ready };
  }

  // Generate quiz using the assessment pipeline
  async function generateQuizFromParams(params: QuizBuilderParams) {
    setGeneratingQuiz(true);
    try {
      const sections = params.questionType === 'Mixed'
        ? [
            { name: 'Section A', numQuestions: Math.ceil(params.numQuestions / 2), marks: Math.ceil(params.totalMarks / 2), questionType: 'MCQ' as any, isMCQ: true },
            { name: 'Section B', numQuestions: Math.floor(params.numQuestions / 2), marks: Math.floor(params.totalMarks / 2), questionType: 'Short Answer' as any }
          ]
        : [{ name: 'Section A', numQuestions: params.numQuestions, marks: params.totalMarks, questionType: params.questionType as any, isMCQ: params.questionType === 'MCQ' }];

      const genParams = {
        examTitle: params.title,
        examType: 'Quiz',
        subject: params.subject,
        topics: params.topics,
        difficulty: params.difficulty,
        totalMarks: params.totalMarks,
        sections
      };

      const userId = auth.currentUser?.uid;
      const questions = await generateQuestions(genParams as any, undefined, userId, true);

      const card: GeneratedQuizCard = { params, questions };
      setPendingQuizCard(card);

      // Add a message with the card attached
      const cardMsg: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `✅ Generated **${questions.length} questions** for "${params.title}". Review below and save when ready.`,
        quizCard: card
      };
      setMessages(prev => [...prev, cardMsg]);
    } catch (err) {
      console.error('[QuizBuilder] Generation failed:', err);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: '❌ Quiz generation failed. Please try again or adjust the parameters.'
      }]);
    } finally {
      setGeneratingQuiz(false);
    }
  }

  // Save quiz to Firebase (same path as IndependentAssessmentView)
  const saveQuizToFirebase = async (card: GeneratedQuizCard) => {
    if (!auth.currentUser) return;
    setSavingQuiz(true);
    try {
      const removeUndefined = (obj: any): any => {
        if (obj === null || obj === undefined) return null;
        if (Array.isArray(obj)) return obj.map(removeUndefined);
        if (typeof obj === 'object') {
          const c: any = {};
          for (const k in obj) c[k] = removeUndefined(obj[k] !== undefined ? obj[k] : null);
          return c;
        }
        return obj;
      };

      // Map generated questions to the assessment schema
      const mappedQuestions = card.questions.map((q: any) => removeUndefined({
        id: q.id || Math.random().toString(36).substr(2, 9),
        type: q.type === 'MCQ' ? 'Multiple Choice' : (q.type || 'Short Answer'),
        text: q.question || q.text || '',
        marks: Number(q.marks) || 2,
        difficulty: q.difficulty || card.params.difficulty,
        options: q.options || null,
        correctAnswerIndex: q.correctAnswerIndex ?? null,
        modelAnswer: q.answer || q.explanation || null,
        section: q.section || 'Section A',
        learningObjective: 'AI Generated via Quick Search'
      }));

      const totalMarks = mappedQuestions.reduce((s: number, q: any) => s + (Number(q.marks) || 0), 0);
      const newRef = doc(collection(db, `teaching_workspaces/${auth.currentUser.uid}/assessments`));
      await setDoc(newRef, {
        title: card.params.title,
        type: 'Quiz',
        instructions: 'Answer all questions.',
        structure: [{ name: 'Section A', count: mappedQuestions.length }],
        questions: mappedQuestions,
        totalMarks,
        duration: '30 Mins',
        status: 'Draft',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Success message
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: `💾 Quiz saved! You can find it in **Assessments** → your quiz will appear as a Draft ready to edit or preview.\n\nWant to create another quiz or ask me something else?`
      }]);
      setPendingQuizCard(null);
      setQuizMode(false);
      setQuizParams({});
      // Refresh saved panel
      loadSavedQuizzes();
    } catch (err) {
      console.error('[QuizBuilder] Save failed:', err);
      alert('Failed to save quiz. Please try again.');
    } finally {
      setSavingQuiz(false);
    }
  };

  // Load saved quizzes for the panel
  const loadSavedQuizzes = async () => {
    if (!auth.currentUser) return;
    setLoadingSaved(true);
    try {
      const q = firestoreQuery(
        collection(db, `teaching_workspaces/${auth.currentUser.uid}/assessments`),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setSavedQuizzes(snap.docs.map(d => ({ id: d.id, ...d.data() } as SavedQuiz)));
    } catch (err) {
      console.error('[QuizBuilder] Load saved failed:', err);
    } finally {
      setLoadingSaved(false);
    }
  };

  const deleteSavedQuiz = async (id: string) => {
    if (!auth.currentUser || !confirm('Delete this quiz?')) return;
    await deleteDoc(doc(db, `teaching_workspaces/${auth.currentUser.uid}/assessments/${id}`));
    setSavedQuizzes(prev => prev.filter(q => q.id !== id));
  };

  // ── PDF Helpers ──
  function extractPDFTitle(content: string): string {
    const lines = content.split('\n');
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const clean = trimmed.replace(/^#+\s*/, '').replace(/\*\*/g, '').replace(/^[-*]\s*/, '');
      const looksLikeQuestion = /\?$/.test(clean) || /^\d+\.\s+/.test(clean);
      const looksLikeSetupText = /^(that sounds|great|got it|i'll|i will|could you|can you|please tell|to make sure|once i have)/i.test(clean);
      if (clean.length > 0 && clean.length < 90 && !looksLikeQuestion && !looksLikeSetupText) return clean;
    }
    return 'Generated Document';
  }

  // ── PDF Generation (manual header button) ──
  const stripPDFMarkdown = (text: string) => String(text || '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1 ($2)')
    .replace(/%%GENERATE_PDF%%/g, '')
    .replace(/%%WORKFLOW_READY%%\w+%%/g, '')
    .trim();

  function isFinalDocumentResponse(content: string) {
    const clean = stripPDFMarkdown(content);
    if (!clean) return false;

    const questionCount = (clean.match(/\?/g) || []).length;
    const setupLanguage = /\b(could you|can you tell me|please tell me|what specific|what do you need|how deep|who(?:'s| is) going to read|any particular|once i have|i'll pull|let me know|to make sure)\b/i;
    const hasDocumentStructure = /(^|\n)\s*(#{1,4}\s+)?(overview|introduction|objectives?|lesson plan|research summary|key findings|recommendations?|references?|conclusion|activities|assessment|materials|rubric|worksheet|answer key)\b/i.test(clean);

    return hasDocumentStructure || (clean.length > 900 && questionCount <= 3 && !setupLanguage.test(clean));
  }

  async function generateProfessionalPDFBlob(title: string, content: string) {
    const cleanTitle = stripPDFMarkdown(title).replace(/^#+\s*/, '') || 'Generated Document';
    const cleanContent = String(content || '')
      .replace(/%%GENERATE_PDF%%/g, '')
      .replace(/%%WORKFLOW_READY%%\w+%%/g, '')
      .trim();

    const htmlBody = marked.parse(cleanContent) as string;

    const css = `
      @page { margin: 0; }
      body {
        font-family: 'Times New Roman', Times, serif;
        font-size: 12pt;
        line-height: 1.65;
        color: #1a1a1a;
        margin: 0;
        padding: 0;
      }
      .pdf-title {
        font-size: 22pt;
        font-weight: bold;
        color: #1a3a5c;
        margin: 0 0 4pt 0;
      }
      .pdf-subtitle {
        font-size: 9pt;
        color: #777;
        margin: 0 0 14pt 0;
        padding: 0 0 8pt 0;
        border-bottom: 1.5px solid #2a5a8c;
      }
      h1 { font-size: 18pt; color: #1a3a5c; margin: 20pt 0 10pt 0; }
      h2 { font-size: 15pt; color: #2a4a6c; margin: 16pt 0 8pt 0; border-bottom: 0.5px solid #ddd; }
      h3 { font-size: 12pt; color: #3a5a7c; margin: 12pt 0 6pt 0; }
      p { margin: 5pt 0; text-align: justify; }
      ul, ol { margin: 5pt 0; padding-left: 24pt; }
      li { margin: 2pt 0; }
      strong { font-weight: bold; }
      blockquote {
        border-left: 2.5px solid #2a5a8c;
        margin: 8pt 0;
        padding: 5pt 12pt;
        background: #f8f9fa;
        font-style: italic;
        color: #333;
      }
      code {
        background: #eee;
        padding: 1pt 4pt;
        font-size: 10pt;
        font-family: 'Courier New', monospace;
      }
      pre {
        background: #f5f5f5;
        padding: 8pt;
        border: 0.5px solid #ddd;
        overflow-x: auto;
      }
      pre code { background: none; padding: 0; }
      table { border-collapse: collapse; width: 100%; margin: 10pt 0; }
      th, td { border: 0.5px solid #ccc; padding: 4pt 8pt; text-align: left; }
      th { background: #f0f4f8; font-weight: bold; }
      hr { border: none; border-top: 0.5px solid #ccc; margin: 14pt 0; }
      img { max-width: 100%; height: auto; }
      a { color: #2563eb; text-decoration: underline; }
    `;

    const dateStr = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${css}</style></head><body>
<div class="pdf-title">${cleanTitle.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>
<p class="pdf-subtitle">Generated: ${dateStr}</p>
${htmlBody}
</body></html>`;

    const element = document.createElement('div');
    element.innerHTML = fullHtml;
    element.style.position = 'absolute';
    element.style.left = '-9999px';
    element.style.top = '0';
    element.style.width = '800px';
    document.body.appendChild(element);

    try {
      const worker = html2pdf()
        .set({
          margin: [20, 15, 16, 15],
          image: { type: 'jpeg', quality: 1 },
          html2canvas: { scale: 2, useCORS: true, letterRendering: true, windowWidth: 800, foreignObjectRendering: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'avoid-all'] }
        } as any)
        .from(element) as any;

      await worker.toPdf();
      const pdf = worker.prop.pdf as jsPDF;
      const totalPages = pdf.getNumberOfPages();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      for (let page = 1; page <= totalPages; page++) {
        pdf.setPage(page);

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(100, 100, 100);
        pdf.text('GENERATED DOCUMENT', 15, 10);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.text(cleanTitle.substring(0, 80), pageWidth - 15, 10, { align: 'right' });

        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.3);
        pdf.line(15, 12.5, pageWidth - 15, 12.5);

        pdf.line(15, pageHeight - 12, pageWidth - 15, pageHeight - 12);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(120, 120, 120);
        pdf.text(`Page ${page} of ${totalPages}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
      }

      const blob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(blob);
      const safeName = cleanTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'generated-document';
      return { blobUrl, fileName: `${safeName}.pdf`, pageCount: totalPages };
    } finally {
      document.body.removeChild(element);
    }
  };

  const handleDownloadPDF = async (messagesToExport?: Message[]) => {
    const msgs = messagesToExport ?? messages;
    if (msgs.length === 0) return;
    setDownloadingPDF(true);
    try {
      const dateStr = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

      const messagesHtml = msgs.map(msg => {
        const isUser = msg.role === 'user';
        const contentHtml = msg.quizCard
          ? `<p><strong>[Quiz Card]</strong> ${msg.quizCard.params.title} — ${msg.quizCard.questions.length} questions, ${msg.quizCard.params.totalMarks} marks</p>`
          : marked.parse(msg.content) as string;
        return `
          <div class="message ${isUser ? 'user-msg' : 'assistant-msg'}">
            <div class="msg-label ${isUser ? 'user-label' : 'assistant-label'}">${isUser ? 'YOU' : 'AI ASSISTANT'}</div>
            <div class="msg-body">${contentHtml}</div>
            <div class="msg-separator"></div>
          </div>`;
      }).join('');

      const css = `
        body {
          font-family: 'Times New Roman', Times, serif;
          font-size: 11pt;
          line-height: 1.6;
          color: #1a1a1a;
          margin: 0;
          padding: 0;
        }
        .conv-title {
          font-size: 20pt;
          font-weight: bold;
          color: #1a3a5c;
          margin: 0 0 4pt 0;
        }
        .conv-subtitle {
          font-size: 9pt;
          color: #777;
          margin: 0 0 14pt 0;
          padding: 0 0 8pt 0;
          border-bottom: 1.5px solid #2a5a8c;
        }
        .message { margin: 8pt 0; padding: 0; }
        .user-msg { }
        .assistant-msg { }
        .msg-label {
          font-size: 8pt;
          font-weight: bold;
          letter-spacing: 0.1em;
          margin-bottom: 2pt;
        }
        .user-label { color: #2563eb; }
        .assistant-label { color: #7c3aed; }
        .msg-body p { margin: 2pt 0; }
        .msg-body ul, .msg-body ol { margin: 2pt 0; padding-left: 20pt; }
        .msg-body li { margin: 1pt 0; }
        .msg-body strong { font-weight: bold; }
        .msg-body code { background: #eee; padding: 1pt 3pt; font-size: 9pt; }
        .msg-body pre { background: #f5f5f5; padding: 6pt; border: 0.5px solid #ddd; }
        .msg-separator {
          border-top: 0.3px solid #e0e0e0;
          margin-top: 6pt;
          padding-top: 2pt;
        }
      `;

      const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${css}</style></head><body>
<div class="conv-title">Quick Search Conversation</div>
<p class="conv-subtitle">Generated: ${dateStr}</p>
${messagesHtml}
</body></html>`;

      const element = document.createElement('div');
      element.innerHTML = fullHtml;
      element.style.position = 'absolute';
      element.style.left = '-9999px';
      element.style.top = '0';
      element.style.width = '800px';
      document.body.appendChild(element);

      try {
        const worker = html2pdf()
          .set({
            margin: [20, 15, 16, 15],
            image: { type: 'jpeg', quality: 1 },
            html2canvas: { scale: 2, useCORS: true, letterRendering: true, windowWidth: 800, foreignObjectRendering: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['css', 'avoid-all'] }
          } as any)
          .from(element) as any;

        await worker.toPdf();
        const pdf = worker.prop.pdf as jsPDF;
        const totalPages = pdf.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        for (let page = 1; page <= totalPages; page++) {
          pdf.setPage(page);

          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(7.5);
          pdf.setTextColor(100, 100, 100);
          pdf.text('QUICK SEARCH', 15, 10);

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(7);
          pdf.text('Conversation Export', pageWidth - 15, 10, { align: 'right' });

          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.3);
          pdf.line(15, 12.5, pageWidth - 15, 12.5);
          pdf.line(15, pageHeight - 12, pageWidth - 15, pageHeight - 12);

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(7.5);
          pdf.setTextColor(120, 120, 120);
          pdf.text(`Page ${page} of ${totalPages}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
        }

        pdf.save('quick-search-conversation.pdf');
      } finally {
        document.body.removeChild(element);
      }
    } catch (err) {
      console.error('[PDF] Generation failed:', err);
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Open saved panel and load
  const handleOpenSaved = () => {
    setSavedFilesOpen(true);
    loadSavedQuizzes();
  };

  async function handleSearch(overrideQuery?: string, overrideAudioBlob?: Blob, overrideImageBlob?: Blob) {
    const textToSearch = overrideQuery || query;
    const activeAudio = overrideAudioBlob || audioBlob;
    const activeImage = overrideImageBlob || imageBlob;

    if (!textToSearch.trim() && !activeAudio && !activeImage) return;

    const activeMedia = activeImage || activeAudio || null;

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: activeImage
        ? `📷 Image uploaded${textToSearch ? ': ' + textToSearch : ' — please analyse this.'}`
        : textToSearch || '',
      isImage: !!activeImage,
      isAudio: !!activeAudio && !activeImage,
      audioUrl: activeAudio ? URL.createObjectURL(activeAudio) : undefined,
    };

    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setQuery('');
    setImageBlob(null);
    setImagePreviewUrl(null);

    try {
      // Detect quiz intent — switch to quiz builder mode
      const isQuizIntent = !activeImage && !activeAudio && QUIZ_KEYWORDS.test(textToSearch);
      if (isQuizIntent && !quizMode && !workflowMode) setQuizMode(true);
      const inQuizMode = quizMode || isQuizIntent;

      // Detect workflow intent — switch to workflow mode
      let detectedWorkflow: WorkflowMode | null = null;
      if (!activeImage && !activeAudio && !quizMode && !workflowMode) {
        for (const [mode, regex] of Object.entries(WORKFLOW_KEYWORDS)) {
          if (regex.test(textToSearch)) {
            detectedWorkflow = mode as WorkflowMode;
            break;
          }
        }
      }
      if (detectedWorkflow && !workflowMode) setWorkflowMode(detectedWorkflow);
      const inWorkflowMode = workflowMode || detectedWorkflow;

      const historyContext = messages.map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n');

      let fullPrompt: string;
      if (activeImage) {
        fullPrompt = textToSearch || '';
      } else if (inQuizMode) {
        fullPrompt = `${QUIZ_BUILDER_SYSTEM_PROMPT}\n\n--- CURRENT PARAMS COLLECTED ---\n${JSON.stringify(quizParams)}\n\n--- CONVERSATION HISTORY ---\n${historyContext}\n\n--- TEACHER SAYS ---\n${textToSearch}`;
      } else if (inWorkflowMode) {
        const activeMode = workflowMode || detectedWorkflow;
        const workflowPrompt = activeMode ? WORKFLOW_PROMPTS[activeMode] : '';
        fullPrompt = `${SYSTEM_PROMPT}\n\n${workflowPrompt}\n\n--- CURRENT PARAMS COLLECTED ---\n${JSON.stringify(workflowParams)}\n\n--- CONVERSATION HISTORY ---\n${historyContext}\n\n--- TEACHER SAYS ---\n${textToSearch}`;
      } else {
        fullPrompt = `${SYSTEM_PROMPT}\n\n--- CONVERSATION HISTORY ---\n${historyContext}\n\n--- NEW QUERY ---\n${textToSearch}`;
      }

      const userId = auth.currentUser?.uid;
      const rawResponse = await geminiMultimodalGenerate(fullPrompt, activeMedia, userId);

      if (audioBlob) setAudioBlob(null);

      // Parse quiz markers from response
      const { clean: responseText, params: extractedParams, ready } = parseQuizMarkers(rawResponse || '');

      // Parse PDF generation marker from raw response
      const shouldGeneratePDF = /%%GENERATE_PDF%%/.test(rawResponse || '');
      let cleanedResponse = responseText.replace(/%%GENERATE_PDF%%/g, '').trim();

      // Parse workflow ready marker
      const workflowReadyMatch = rawResponse.match(/%%WORKFLOW_READY%%(\w+)%%/);
      const isWorkflowReady = !!workflowReadyMatch;
      if (workflowReadyMatch) {
        cleanedResponse = cleanedResponse.replace(/%%WORKFLOW_READY%%\w+%%/g, '').trim();
      }

      if (extractedParams) {
        const merged = { ...quizParams, ...extractedParams };
        setQuizParams(merged);

        // If ready and have all params, auto-generate
        if (ready && merged.subject && merged.topics?.length && merged.numQuestions && merged.questionType && merged.difficulty && merged.totalMarks) {
          const aiMsg: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: cleanedResponse || 'Perfect! Generating your quiz now…'
          };
          setMessages(prev => [...prev, aiMsg]);
          setIsLoading(false);
          await generateQuizFromParams(merged as QuizBuilderParams);
          return;
        }
      }

      // Handle workflow ready — generate output and create card
      if (isWorkflowReady) {
        const mode = (workflowReadyMatch?.[1] || workflowMode) as WorkflowMode;

        if (!isFinalDocumentResponse(cleanedResponse)) {
          const aiMsg: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: cleanedResponse || 'I still need a few details before I can generate the final document.'
          };
          setMessages(prev => [...prev, aiMsg]);
          setWorkflowMode(mode);
          setIsLoading(false);
          return;
        }

        const title = extractPDFTitle(cleanedResponse);
        const pdfData = await generateProfessionalPDFBlob(title, cleanedResponse);

        const workflowCard: WorkflowCard = {
          mode,
          title,
          params: { ...workflowParams },
          content: cleanedResponse,
          blobUrl: pdfData.blobUrl,
          fileName: pdfData.fileName
        };

        // Save to version history
        setSavedVersions(prev => [{
          id: Date.now().toString(),
          title,
          mode,
          content: cleanedResponse,
          date: new Date(),
          blobUrl: pdfData.blobUrl
        }, ...prev]);

        const aiMsg: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: cleanedResponse || 'I apologise, but I could not generate a response. Please try again.',
          workflowCard,
          pdfCard: { title, blobUrl: pdfData.blobUrl, fileName: pdfData.fileName, pageCount: pdfData.pageCount }
        };
        setMessages(prev => [...prev, aiMsg]);
        setWorkflowMode(null);
        setWorkflowParams({});
        setIsLoading(false);
        return;
      }

      // Generate PDF card if AI included the marker
      let pdfCardData: GeneratedPDFCard | undefined;
      if (shouldGeneratePDF && cleanedResponse && !isWorkflowReady && isFinalDocumentResponse(cleanedResponse)) {
        const title = extractPDFTitle(cleanedResponse);
        const pdfData = await generateProfessionalPDFBlob(title, cleanedResponse);
        pdfCardData = { title, blobUrl: pdfData.blobUrl, fileName: pdfData.fileName, pageCount: pdfData.pageCount };
      }

      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: cleanedResponse || 'I apologise, but I could not generate a response. Please try again.',
        pdfCard: pdfCardData
      };
      setMessages(prev => [...prev, aiMsg]);

      if (audioBlob && rawResponse) {
        const utterance = new SpeechSynthesisUtterance(rawResponse.slice(0, 400));
        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I encountered an error processing your request. Please check your connection and try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  // --- Voice Input Implementation ---
  // --- Audio Recording (MediaRecorder) ---

  const startRecording = async () => {
    console.log("[IndependentSearch] startRecording initiated");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const chunks = audioChunksRef.current;
        const blob = new Blob(chunks, { type: 'audio/webm' });
        console.log(`[IndependentSearch] Recording stopped. Blob created: ${blob.type}, size: ${blob.size}`);
        setAudioBlob(blob);
        // Automatically trigger search with audio
        handleSearch("", blob); 
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
       console.error("Error accessing microphone:", error);
       alert("Could not access microphone.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // --- Text-to-Speech Implementation ---

  const handleSpeak = (text: string, id: string) => {
    if (speakingId === id) {
      // Stop speaking
      window.speechSynthesis.cancel();
      setSpeakingId(null);
      return;
    }

    // Cancel current speech if any
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.onend = () => setSpeakingId(null);
    utterance.onerror = () => setSpeakingId(null);
    
    setSpeakingId(id);
    window.speechSynthesis.speak(utterance);
  };

  // --- Image Upload Simulation ---

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Only accept images
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPG, PNG, WEBP, etc.)');
      return;
    }
    const blob = new Blob([file], { type: file.type });
    setImageBlob(blob);
    const url = URL.createObjectURL(blob);
    setImagePreviewUrl(url);
    // Reset input so the same file can be selected again if needed
    e.target.value = '';
  };

  return (
    <div className="absolute inset-0 flex flex-col bg-gray-50 dark:bg-[#020617] overflow-hidden">

      {/* ── Header ── */}
      <div className="flex-none flex items-center justify-between px-5 py-2.5 border-b border-gray-200 dark:border-white/5 bg-white dark:bg-[#020617] z-20">
        <div className="flex items-center gap-3">
          <button
            onClick={() => quickSearchReturn ? navigate(quickSearchReturn.path, { replace: true, state: quickSearchReturn.state }) : navigate('/dashboard/utilities', { replace: true })}
            className="h-8 w-8 flex items-center justify-center text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
            title={quickSearchReturn ? 'Back to captured document' : 'Back to utilities'}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-px h-4 bg-gray-200 dark:bg-white/10" />
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-700 dark:text-zinc-300">Quick Search</span>
            {quickSearchSourceLabel && (
              <span className="hidden sm:inline text-[9px] font-bold uppercase tracking-widest text-blue-500/70 bg-blue-500/10 px-2 py-0.5 rounded">
                Capture · {quickSearchSourceLabel}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Saved Files button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleOpenSaved}
                className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-violet-500 dark:hover:text-violet-400 transition-colors px-2 py-1"
              >
                <BookMarked className="w-3 h-3" />
                Saved
              </button>
            </TooltipTrigger>
            <TooltipContent><p className="text-[10px]">View your saved quizzes and assessments</p></TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => handleDownloadPDF()}
                disabled={messages.length === 0 || downloadingPDF}
                className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors px-2 py-1 disabled:opacity-30 disabled:pointer-events-none"
              >
                {downloadingPDF ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {downloadingPDF ? 'Exporting…' : 'PDF'}
              </button>
            </TooltipTrigger>
            <TooltipContent><p className="text-[10px]">Export this conversation as a downloadable PDF</p></TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setWorkflowPanelOpen(!workflowPanelOpen)}
                className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-amber-500 dark:hover:text-amber-400 transition-colors px-2 py-1"
              >
                <PanelLeft className="w-3 h-3" />
                Workflows
              </button>
            </TooltipTrigger>
            <TooltipContent><p className="text-[10px]">Open the workflow panel to start a guided lesson plan, rubric, or worksheet</p></TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setTemplateLibraryOpen(true)}
                className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors px-2 py-1"
              >
                <Grid3X3 className="w-3 h-3" />
                Templates
              </button>
            </TooltipTrigger>
            <TooltipContent><p className="text-[10px]">Browse pre-built templates like 5E Lesson Plans, Rubrics, and Worksheets</p></TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setVersionHistoryOpen(true)}
                className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-violet-500 dark:hover:text-violet-400 transition-colors px-2 py-1"
              >
                <History className="w-3 h-3" />
                History
              </button>
            </TooltipTrigger>
            <TooltipContent><p className="text-[10px]">View version history of previously generated documents</p></TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setResourceSharingOpen(true)}
                className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors px-2 py-1"
              >
                <Share2 className="w-3 h-3" />
                Share
              </button>
            </TooltipTrigger>
            <TooltipContent><p className="text-[10px]">Share or export your content via link, PDF, or Google Drive</p></TooltipContent>
          </Tooltip>
          <div className="w-px h-3 bg-gray-200 dark:bg-white/10" />
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 cursor-default">
                <Globe className="w-3 h-3" />
                En
              </button>
            </TooltipTrigger>
            <TooltipContent><p className="text-[10px]">Current response language: English (multi-language auto-detection enabled)</p></TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* ── Chat Area ── */}
      <div className="flex-1 overflow-y-auto scroll-smooth">
        <div className="max-w-3xl mx-auto p-4 sm:p-8 space-y-5">
          {messages.length === 0 ? (
            <div className="text-center py-16 px-4">
              {/* Icon */}
              <div className="w-14 h-14 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center mx-auto mb-6">
                <Search className="w-6 h-6 text-blue-500" />
              </div>
              <h2 className="text-xl font-black uppercase tracking-tighter text-slate-900 dark:text-white mb-2">
                How can I help you teach today?
              </h2>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-600 max-w-sm mx-auto mb-8">
                Ask about lesson concepts, scan worksheets, solve problems, or translate instructions.
              </p>

              {/* Suggestion chips */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg mx-auto text-left">
                {[
                  "Scan this math worksheet question...",
                  "Explain photosynthesis to a 5th grader",
                  "Translate this note to Spanish",
                  "Create a quiz for Algebra I"
                ].map((hint, i) => (
                  <button
                    key={i}
                    onClick={() => handleSearch(hint)}
                    className="p-3 text-left text-[10px] font-bold uppercase tracking-wider border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.02] hover:bg-gray-50 dark:hover:bg-white/5 hover:border-blue-500/40 dark:hover:border-blue-500/30 transition-all text-slate-500 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
                  >
                    {hint}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5 pb-4">

              {/* ── Quiz mode banner ── */}
              {quizMode && (
                <div className="flex items-center justify-between bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-500/20 px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileQuestion className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-violet-600 dark:text-violet-400">Quiz Builder Active</span>
                    {quizParams.subject && (
                      <span className="text-[9px] text-violet-400/70 truncate">
                        · {quizParams.subject}
                        {quizParams.topics?.length ? ' · ' + quizParams.topics.join(', ') : ''}
                        {quizParams.numQuestions ? ' · ' + quizParams.numQuestions + 'Q' : ''}
                        {quizParams.difficulty ? ' · ' + quizParams.difficulty : ''}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => { setQuizMode(false); setQuizParams({}); }}
                    className="text-violet-400 hover:text-red-500 transition-colors ml-2 shrink-0"
                    title="Exit quiz builder"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* ── Workflow mode banner ── */}
              {workflowMode && (
                <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/20 px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Lightbulb className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">
                      {WORKFLOW_ACTIVE_NAMES[workflowMode]} Active
                    </span>
                  </div>
                  <button
                    onClick={() => { setWorkflowMode(null); setWorkflowParams({}); }}
                    className="text-amber-400 hover:text-red-500 transition-colors ml-2 shrink-0"
                    title="Exit workflow mode"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* ── Generating workflow indicator ── */}
              {generatingWorkflow && (
                <div className="flex items-center gap-3 bg-white dark:bg-[#0a1024] border border-amber-200 dark:border-amber-500/20 px-4 py-3">
                  <Loader2 className="w-4 h-4 text-amber-500 animate-spin shrink-0" />
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">Generating…</p>
                    <p className="text-[9px] text-slate-400 dark:text-slate-600 mt-0.5">Crafting your resource — this may take a moment.</p>
                  </div>
                </div>
              )}

              {/* ── Generating quiz indicator ── */}
              {generatingQuiz && (
                <div className="flex items-center gap-3 bg-white dark:bg-[#0a1024] border border-violet-200 dark:border-violet-500/20 px-4 py-3">
                  <Loader2 className="w-4 h-4 text-violet-500 animate-spin shrink-0" />
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-violet-600 dark:text-violet-400">Generating Quiz…</p>
                    <p className="text-[9px] text-slate-400 dark:text-slate-600 mt-0.5">Crafting your questions — this may take 10–20 seconds.</p>
                  </div>
                </div>
              )}

              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {/* AI Avatar */}
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 bg-blue-600/10 dark:bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0 mt-1">
                      <Sparkles className="w-3.5 h-3.5 text-blue-500" />
                    </div>
                  )}

                  {/* Bubble */}
                  <div className={`text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'max-w-[85%] sm:max-w-[75%] px-4 py-3 bg-blue-600 text-white'
                      : msg.quizCard || msg.pdfCard || msg.workflowCard
                        ? 'w-full max-w-2xl'
                        : 'max-w-[85%] sm:max-w-[75%] px-4 py-3 bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/8 text-slate-800 dark:text-zinc-200'
                  }`}>
                    {/* Quiz card rendering */}
                    {msg.quizCard ? (
                      <div className="bg-white dark:bg-[#0a1024] border border-violet-200 dark:border-violet-500/20 overflow-hidden">
                        {/* Card header */}
                        <div className="flex items-center justify-between px-4 py-3 bg-violet-50 dark:bg-violet-900/10 border-b border-violet-200 dark:border-violet-500/20">
                          <div className="flex items-center gap-2">
                            <ClipboardList className="w-4 h-4 text-violet-500" />
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-widest text-violet-700 dark:text-violet-300">{msg.quizCard.params.title}</p>
                              <p className="text-[9px] text-violet-500/70">{msg.quizCard.params.subject} · {msg.quizCard.questions.length} questions · {msg.quizCard.params.difficulty} · {msg.quizCard.params.totalMarks} marks</p>
                            </div>
                          </div>
                          <span className="text-[8px] font-black uppercase tracking-widest px-2 py-0.5 bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300">
                            {msg.quizCard.params.questionType}
                          </span>
                        </div>

                        {/* Questions preview */}
                        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-white/5">
                          {msg.quizCard.questions.map((q: any, idx: number) => (
                            <div key={q.id || idx} className="px-4 py-2.5">
                              <div className="flex items-start gap-2">
                                <span className="text-[9px] font-black text-slate-400 dark:text-slate-600 shrink-0 mt-0.5">Q{idx + 1}</span>
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs text-slate-700 dark:text-zinc-300 leading-relaxed">{q.question || q.text}</p>
                                  {q.type === 'MCQ' && q.options && (
                                    <div className="mt-1.5 grid grid-cols-2 gap-1">
                                      {q.options.map((opt: string, oi: number) => (
                                        <span key={oi} className={`text-[9px] px-2 py-0.5 border ${
                                          q.answer === String.fromCharCode(65 + oi)
                                            ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                                            : 'border-gray-200 dark:border-white/10 text-slate-500 dark:text-slate-500'
                                        }`}>{String.fromCharCode(65 + oi)}. {opt}</span>
                                      ))}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-[8px] text-slate-400">{q.marks} mk</span>
                                    <span className="text-[8px] text-slate-400">{q.difficulty}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-white/[0.02] border-t border-gray-100 dark:border-white/5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => saveQuizToFirebase(msg.quizCard!)}
                                disabled={savingQuiz}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-[9px] font-black uppercase tracking-widest transition-all disabled:opacity-50"
                              >
                                {savingQuiz ? <Loader2 className="w-3 h-3 animate-spin text-white" /> : <Save className="w-3 h-3" />}
                                Save to Assessments
                              </button>
                            </TooltipTrigger>
                            <TooltipContent><p className="text-[10px]">Save this quiz to your Assessments library as a draft</p></TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => {
                                  setPendingQuizCard(null);
                                  setMessages(prev => [...prev, {
                                    id: Date.now().toString(),
                                    role: 'assistant',
                                    content: 'Quiz discarded. Want to try again with different settings?'
                                  }]);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-white/10 text-[9px] font-black uppercase tracking-widest text-slate-500 hover:text-red-500 hover:border-red-400 transition-all"
                              >
                                <Trash2 className="w-3 h-3" />
                                Discard
                              </button>
                            </TooltipTrigger>
                            <TooltipContent><p className="text-[10px]">Discard this generated quiz. This cannot be undone.</p></TooltipContent>
                          </Tooltip>
                          <span className="ml-auto text-[8px] text-slate-400 dark:text-slate-600">{msg.quizCard.questions.length} questions generated</span>
                        </div>
                      </div>
                    ) : msg.pdfCard && !msg.workflowCard ? (
                      <div className="px-4 py-3 bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/8">
                        <MessageContent content={msg.content} role={msg.role} />
                        {/* PDF download card */}
                        <div className="mt-4 bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-500/20 overflow-hidden">
                          <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 dark:bg-emerald-900/10 border-b border-emerald-200 dark:border-emerald-500/20">
                            <div className="flex items-center gap-2 min-w-0">
                              <Download className="w-4 h-4 text-emerald-500 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300 truncate">{msg.pdfCard.title}</p>
                                <p className="text-[9px] text-emerald-500/70">{msg.pdfCard.pageCount} page{msg.pdfCard.pageCount !== 1 ? 's' : ''} · PDF</p>
                              </div>
                            </div>
                            <a
                              href={msg.pdfCard.blobUrl}
                              download={msg.pdfCard.fileName}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest transition-all shrink-0"
                            >
                              <Download className="w-3 h-3" />
                              Download
                            </a>
                          </div>
                        </div>
                        {msg.role === 'assistant' && (
                          <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-white/5 flex items-center gap-1.5">
                            <button
                              onClick={() => handleSpeak(msg.content, msg.id)}
                              title={speakingId === msg.id ? 'Stop reading' : 'Read aloud'}
                              className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                            >
                              {speakingId === msg.id ? (
                                <StopCircle className="h-3 w-3 text-red-500 animate-pulse" />
                              ) : (
                                <Volume2 className="h-3 w-3" />
                              )}
                              {speakingId === msg.id ? 'Stop' : 'Listen'}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : msg.workflowCard ? (
                      <div className="px-4 py-3 bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/8">
                        <MessageContent content={msg.content} role={msg.role} />
                        {/* Workflow output card */}
                        <div className="mt-4 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/20 overflow-hidden">
                          <div className="flex items-center justify-between px-4 py-3 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-200 dark:border-amber-500/20">
                            <div className="flex items-center gap-2 min-w-0">
                              <Lightbulb className="w-4 h-4 text-amber-500 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-300 truncate">
                                  {WORKFLOW_DISPLAY_NAMES[msg.workflowCard.mode]} — {msg.workflowCard.title}
                                </p>
                                <p className="text-[9px] text-amber-500/70">Ready to use · PDF available</p>
                              </div>
                            </div>
                            {msg.workflowCard.blobUrl && (
                              <a
                                href={msg.workflowCard.blobUrl}
                                download={msg.workflowCard.fileName || 'document.pdf'}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-[9px] font-black uppercase tracking-widest transition-all shrink-0"
                              >
                                <Download className="w-3 h-3" />
                                Download PDF
                              </a>
                            )}
                          </div>
                        </div>
                        {msg.role === 'assistant' && (
                          <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-white/5 flex items-center gap-1.5">
                            <button
                              onClick={() => handleSpeak(msg.content, msg.id)}
                              title={speakingId === msg.id ? 'Stop reading' : 'Read aloud'}
                              className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                            >
                              {speakingId === msg.id ? (
                                <StopCircle className="h-3 w-3 text-red-500 animate-pulse" />
                              ) : (
                                <Volume2 className="h-3 w-3" />
                              )}
                              {speakingId === msg.id ? 'Stop' : 'Listen'}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : msg.isAudio ? (
                      <AudioMessageBubble audioUrl={msg.audioUrl} />
                    ) : (
                      <>
                        <MessageContent content={msg.content} role={msg.role} />
                        {msg.role === 'assistant' && (
                          <div className="mt-2.5 pt-2.5 border-t border-gray-100 dark:border-white/5 flex items-center gap-1.5">
                            <button
                              onClick={() => handleSpeak(msg.content, msg.id)}
                              title={speakingId === msg.id ? 'Stop reading' : 'Read aloud'}
                              className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                            >
                              {speakingId === msg.id ? (
                                <StopCircle className="h-3 w-3 text-red-500 animate-pulse" />
                              ) : (
                                <Volume2 className="h-3 w-3" />
                              )}
                              {speakingId === msg.id ? 'Stop' : 'Listen'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* User Avatar */}
                  {msg.role === 'user' && (
                    <div className="w-7 h-7 bg-slate-200 dark:bg-white/10 border border-slate-300 dark:border-white/10 flex items-center justify-center shrink-0 mt-1">
                      <div className="w-3 h-3 rounded-full bg-slate-400 dark:bg-slate-500" />
                    </div>
                  )}
                </motion.div>
              ))}

              {/* Typing indicator */}
              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 bg-blue-600/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                    <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />
                  </div>
                  <div className="bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/8 px-4 py-3 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-600 animate-bounce [animation-delay:0ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-600 animate-bounce [animation-delay:150ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-600 animate-bounce [animation-delay:300ms]" />
                  </div>
                </div>
              )}
              <div ref={scrollRef} />
            </div>
          )}
        </div>
      </div>

      {/* ── Input Area (Floating) ── */}
      <div className="flex-none px-4 pb-4 pt-6 bg-gradient-to-t from-gray-50 dark:from-[#020617] via-gray-50/80 dark:via-[#020617]/80 to-transparent z-10">
        <div className="max-w-3xl mx-auto">

          {/* Image preview pill — shown above the input bar when an image is staged */}
          {imagePreviewUrl && (
            <div className="mb-2 flex items-center gap-2 px-1">
              <div className="relative flex items-center gap-2 bg-white dark:bg-[#0a1024] border border-violet-300 dark:border-violet-500/30 px-2 py-1.5 max-w-xs">
                <img
                  src={imagePreviewUrl}
                  alt="Staged image"
                  className="h-8 w-8 object-cover"
                />
                <div className="min-w-0">
                  <p className="text-[9px] font-black uppercase tracking-widest text-violet-600 dark:text-violet-400">Image ready</p>
                  <p className="text-[8px] text-slate-400 dark:text-slate-600 truncate">Send with a question or alone to analyse</p>
                </div>
                <button
                  onClick={() => { setImageBlob(null); setImagePreviewUrl(null); }}
                  className="ml-1 text-slate-400 hover:text-red-500 transition-colors"
                  title="Remove image"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          <div className={`flex gap-2 items-end bg-white dark:bg-[#0a1024] border transition-all ${
            isRecording
              ? 'border-red-500/60 shadow-[0_0_0_2px_rgba(239,68,68,0.15)]'
              : imageBlob
                ? 'border-violet-400/60 dark:border-violet-500/30 shadow-[0_0_0_2px_rgba(139,92,246,0.1)]'
                : 'border-gray-200 dark:border-white/10 focus-within:border-blue-500/50 dark:focus-within:border-blue-500/30 focus-within:shadow-[0_0_0_2px_rgba(59,130,246,0.1)]'
          }`}>

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />

            {/* Image upload button — violet tint when image is staged */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleImageClick}
                  title={imageBlob ? 'Replace image' : 'Upload image from board or worksheet'}
                  className={`h-10 w-10 flex items-center justify-center transition-colors shrink-0 ${
                    imageBlob
                      ? 'text-violet-500 dark:text-violet-400'
                      : 'text-slate-400 dark:text-slate-600 hover:text-violet-500 dark:hover:text-violet-400'
                  }`}
                >
                  <Camera className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Upload an image of a worksheet, whiteboard, or textbook page for AI analysis</p></TooltipContent>
            </Tooltip>

            {/* Textarea */}
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isRecording
                  ? 'Listening… click mic to stop'
                  : imageBlob
                    ? 'Add a question about this image, or send to auto-analyse…'
                    : 'Ask anything, or upload an image from the board…'
              }
              className="flex-1 min-w-0 min-h-[2.25rem] max-h-32 border-0 bg-transparent focus-visible:ring-0 resize-none py-2.5 px-0 text-sm text-slate-800 dark:text-zinc-200 placeholder:text-slate-400 dark:placeholder:text-slate-600"
              rows={1}
            />

            {/* Controls */}
            <div className="flex items-center gap-1 pr-1.5 pb-1.5 shrink-0">
              {/* Mic / Stop */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={toggleRecording}
                    title={isRecording ? 'Stop and send recording' : 'Start voice recording'}
                    className={`h-8 w-8 flex items-center justify-center transition-all ${
                      isRecording
                        ? 'text-white bg-red-500 hover:bg-red-600 rounded-full animate-pulse shadow-lg shadow-red-500/30'
                        : 'text-slate-400 dark:text-slate-600 hover:text-blue-500 dark:hover:text-blue-400'
                    }`}
                  >
                    {isRecording ? (
                      <Square className="w-3 h-3 fill-current" />
                    ) : (
                      <Mic className="w-3.5 h-3.5" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent><p className="text-[10px]">{isRecording ? 'Click to stop and send' : 'Click to start voice input'}</p></TooltipContent>
              </Tooltip>

              {/* Send — enabled when there's text OR a staged image */}
              <button
                onClick={() => handleSearch()}
                title="Send query"
                disabled={(!query.trim() && !imageBlob) || isLoading}
                className="h-8 w-8 flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-700 mt-2 text-center">
            AI responses may be inaccurate — always verify critical content.
          </p>
        </div>
      </div>

      {/* ── Saved Files Panel ── */}
      <AnimatePresence>
        {savedFilesOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSavedFilesOpen(false)}
              className="absolute inset-0 bg-black/20 dark:bg-black/40 z-30"
            />

            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-[#040d1f] border-l border-gray-200 dark:border-white/8 z-40 flex flex-col"
            >
              {/* Panel header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-2">
                  <BookMarked className="w-3.5 h-3.5 text-violet-500" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-zinc-300">Saved Assessments</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={loadSavedQuizzes}
                    disabled={loadingSaved}
                    className="text-slate-400 hover:text-blue-500 transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingSaved ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => setSavedFilesOpen(false)}
                    title="Close saved files"
                    className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Go to Assessments link */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate('/dashboard/assessment')}
                    className="flex items-center justify-between px-4 py-2.5 bg-violet-50 dark:bg-violet-900/10 border-b border-violet-100 dark:border-violet-500/10 hover:bg-violet-100 dark:hover:bg-violet-900/20 transition-colors group"
                  >
                    <span className="text-[9px] font-black uppercase tracking-widest text-violet-600 dark:text-violet-400">Open Full Assessments View</span>
                    <ChevronRight className="w-3 h-3 text-violet-400 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </TooltipTrigger>
                <TooltipContent><p className="text-[10px]">Navigate to the full Assessments dashboard to manage your quizzes</p></TooltipContent>
              </Tooltip>

              {/* List */}
              <div className="flex-1 overflow-y-auto">
                {loadingSaved ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
                  </div>
                ) : savedQuizzes.length === 0 ? (
                  <div className="text-center py-16 px-4">
                    <ClipboardList className="w-8 h-8 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">No saved assessments yet</p>
                    <p className="text-[9px] text-slate-400 dark:text-slate-600 mt-1">Ask me to create a quiz to get started.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-white/5">
                    {savedQuizzes.map(quiz => (
                      <div key={quiz.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors group">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-bold text-slate-700 dark:text-zinc-300 truncate">{quiz.title}</p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400">{quiz.type || 'Quiz'}</span>
                              <span className="text-[8px] text-slate-400">{quiz.questions?.length || 0} questions</span>
                              <span className="text-[8px] text-slate-400">{quiz.totalMarks || 0} marks</span>
                              <span className={`text-[8px] font-bold ${quiz.status === 'Published' ? 'text-emerald-500' : 'text-amber-500'}`}>{quiz.status}</span>
                            </div>
                            {quiz.createdAt?.toDate && (
                              <p className="text-[8px] text-slate-400 dark:text-slate-600 mt-0.5">
                                {quiz.createdAt.toDate().toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                            <button
                              onClick={() => navigate('/dashboard/assessment', { state: { openAssessmentId: quiz.id } })}
                              className="p-1 text-slate-400 hover:text-blue-500 transition-colors"
                              title="Open in Assessments"
                            >
                              <Eye className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => deleteSavedQuiz(quiz.id)}
                              className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-2.5 border-t border-gray-100 dark:border-white/5">
                <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-600 text-center">
                  {savedQuizzes.length} assessment{savedQuizzes.length !== 1 ? 's' : ''} saved
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Workflow Panel ── */}
      <AnimatePresence>
        {workflowPanelOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setWorkflowPanelOpen(false)}
              className="absolute inset-0 bg-black/20 dark:bg-black/40 z-30"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-[#040d1f] border-l border-gray-200 dark:border-white/8 z-40 flex flex-col"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-zinc-300">Workflows</span>
                </div>
                <button
                  onClick={() => setWorkflowPanelOpen(false)}
                  title="Close workflows"
                  className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {/* Core Workflows */}
                <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">Core</p>
                {[
                  { id: 'lesson' as WorkflowMode, icon: BookOpen, label: 'Lesson & Curriculum Planning', desc: 'Full lesson plans, schemes of work, unit plans with objectives, activities, timing, differentiation', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/10', border: 'border-blue-200 dark:border-blue-500/20' },
                  { id: 'content' as WorkflowMode, icon: FileText, label: 'Content Creation & Adaptation', desc: 'Lecture notes, student handouts, summaries, textbook adaptations, reading comprehension', color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/10', border: 'border-emerald-200 dark:border-emerald-500/20' },
                  { id: 'interactive' as WorkflowMode, icon: Users, label: 'Interactive & Engaging Resources', desc: 'Flashcards, discussion prompts, debates, role-plays, gamification, group activities, projects', color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/10', border: 'border-purple-200 dark:border-purple-500/20' },
                  { id: 'admin' as WorkflowMode, icon: Mail, label: 'Administrative Support', desc: 'Emails to parents, report card comments, meeting agendas, proposals, permission letters', color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/10', border: 'border-orange-200 dark:border-orange-500/20' },
                  { id: 'research' as WorkflowMode, icon: GraduationCap, label: 'Research & Professional Development', desc: 'Literature reviews, conference papers, policy briefs, academic articles, PD materials', color: 'text-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-900/10', border: 'border-cyan-200 dark:border-cyan-500/20' },
                  { id: 'student' as WorkflowMode, icon: UserCheck, label: 'Student Support Features', desc: 'Personalized study plans, remedial worksheets, extension materials, career guidance', color: 'text-pink-500', bg: 'bg-pink-50 dark:bg-pink-900/10', border: 'border-pink-200 dark:border-pink-500/20' },
                  { id: 'rubric' as WorkflowMode, icon: Ruler, label: 'Rubric Generator', desc: 'Clear grading rubrics with criteria, performance levels, and descriptors', color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-900/10', border: 'border-indigo-200 dark:border-indigo-500/20' },
                  { id: 'homework' as WorkflowMode, icon: Pencil, label: 'Homework & Assignments', desc: 'Meaningful homework with practice problems, reading, writing tasks + answer key', color: 'text-teal-500', bg: 'bg-teal-50 dark:bg-teal-900/10', border: 'border-teal-200 dark:border-teal-500/20' },
                  { id: 'reference' as WorkflowMode, icon: Bookmark, label: 'Quick Reference Cards', desc: 'One-page cheat sheets for formulas, vocabulary, key concepts, steps', color: 'text-rose-500', bg: 'bg-rose-50 dark:bg-rose-900/10', border: 'border-rose-200 dark:border-rose-500/20' },
                  { id: 'parentEmail' as WorkflowMode, icon: MessageSquare, label: 'Parent Email Templates', desc: 'Pre-built templates for progress updates, concerns, celebrations, requests', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/10', border: 'border-amber-200 dark:border-amber-500/20' },
                  { id: 'differentiation' as WorkflowMode, icon: GitBranch, label: 'Differentiation Matrix', desc: '3 versions of lessons — support, core, extension — for mixed-ability classes', color: 'text-violet-500', bg: 'bg-violet-50 dark:bg-violet-900/10', border: 'border-violet-200 dark:border-violet-500/20' },
                  { id: 'feedback' as WorkflowMode, icon: MessageCircle, label: 'Student Work Feedback', desc: 'Constructive feedback aligned to rubric criteria with actionable next steps', color: 'text-sky-500', bg: 'bg-sky-50 dark:bg-sky-900/10', border: 'border-sky-200 dark:border-sky-500/20' },
                  { id: 'reflection' as WorkflowMode, icon: NotebookPen, label: 'Lesson Reflection Journal', desc: 'Post-lesson reflection with strengths, growth areas, and improvement suggestions', color: 'text-lime-500', bg: 'bg-lime-50 dark:bg-lime-900/10', border: 'border-lime-200 dark:border-lime-500/20' },
                  { id: 'analytics' as WorkflowMode, icon: BarChart3, label: 'Assessment Analytics', desc: 'Quiz/test analysis with common misconceptions and reteaching recommendations', color: 'text-fuchsia-500', bg: 'bg-fuchsia-50 dark:bg-fuchsia-900/10', border: 'border-fuchsia-200 dark:border-fuchsia-500/20' },
                  { id: 'standards' as WorkflowMode, icon: Scale, label: 'Standards Alignment', desc: 'Map your content to curriculum standards — CBC, IB, A-Level, Common Core', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/10', border: 'border-emerald-200 dark:border-emerald-500/20' }
                ].map(wf => {
                  const Icon = wf.icon;
                  const isActive = workflowMode === wf.id;
                  return (
                    <button
                      key={wf.id}
                      onClick={() => {
                        setWorkflowMode(wf.id);
                        setWorkflowPanelOpen(false);
                        setQuery(`I need help with ${wf.label.toLowerCase()}.`);
                      }}
                      className={`w-full text-left p-3 border ${wf.border} ${wf.bg} hover:opacity-80 transition-all ${isActive ? 'ring-2 ring-amber-400' : ''}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <Icon className={`w-4 h-4 ${wf.color} shrink-0 mt-0.5`} />
                        <div className="min-w-0">
                          <p className={`text-[10px] font-black uppercase tracking-widest ${wf.color}`}>{wf.label}</p>
                          <p className="text-[9px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{wf.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="px-4 py-2.5 border-t border-gray-100 dark:border-white/5">
                <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-600 text-center">
                  Select a workflow to start a guided session
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Template Library Panel ── */}
      <AnimatePresence>
        {templateLibraryOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setTemplateLibraryOpen(false)}
              className="absolute inset-0 bg-black/20 dark:bg-black/40 z-30"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-[#040d1f] border-l border-gray-200 dark:border-white/8 z-40 flex flex-col"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-2">
                  <Grid3X3 className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-zinc-300">Template Library</span>
                </div>
                <button
                  onClick={() => setTemplateLibraryOpen(false)}
                  title="Close template library"
                  className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {[
                  { icon: BookOpen, label: '5E Lesson Plan', desc: 'Engage, Explore, Explain, Elaborate, Evaluate', mode: 'lesson', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/10' },
                  { icon: FileText, label: 'Student Handout', desc: 'Clean, structured handout with sections', mode: 'content', color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/10' },
                  { icon: Ruler, label: '4-Level Rubric', desc: 'Excellent, Good, Satisfactory, Needs Improvement', mode: 'rubric', color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-900/10' },
                  { icon: Pencil, label: 'Practice Worksheet', desc: 'Graduated difficulty with answer key', mode: 'homework', color: 'text-teal-500', bg: 'bg-teal-50 dark:bg-teal-900/10' },
                  { icon: Bookmark, label: 'Formula Sheet', desc: 'One-page reference with key formulas', mode: 'reference', color: 'text-rose-500', bg: 'bg-rose-50 dark:bg-rose-900/10' },
                  { icon: MessageSquare, label: 'Progress Email', desc: 'Parent update on student progress', mode: 'parentEmail', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/10' },
                  { icon: GitBranch, label: 'Differentiated Lesson', desc: 'Support, Core, Extension versions', mode: 'differentiation', color: 'text-violet-500', bg: 'bg-violet-50 dark:bg-violet-900/10' },
                  { icon: Users, label: 'Group Activity', desc: 'Collaborative task with roles and timing', mode: 'interactive', color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/10' },
                  { icon: BarChart3, label: 'Quiz Analysis', desc: 'Results breakdown with misconceptions', mode: 'analytics', color: 'text-fuchsia-500', bg: 'bg-fuchsia-50 dark:bg-fuchsia-900/10' },
                  { icon: NotebookPen, label: 'Lesson Reflection', desc: 'What worked, what didn\'t, next steps', mode: 'reflection', color: 'text-lime-500', bg: 'bg-lime-50 dark:bg-lime-900/10' },
                  { icon: Scale, label: 'CBC Standards Map', desc: 'Competency-based curriculum alignment', mode: 'standards', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/10' },
                  { icon: GraduationCap, label: 'Research Summary', desc: 'Literature review with key findings', mode: 'research', color: 'text-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-900/10' }
                ].map((tpl, i) => {
                  const Icon = tpl.icon;
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        setWorkflowMode(tpl.mode as WorkflowMode);
                        setTemplateLibraryOpen(false);
                        setQuery(`Create a ${tpl.label.toLowerCase()} template.`);
                      }}
                      className={`w-full text-left p-3 border border-gray-100 dark:border-white/8 ${tpl.bg} hover:opacity-80 transition-all`}
                    >
                      <div className="flex items-start gap-2.5">
                        <Icon className={`w-4 h-4 ${tpl.color} shrink-0 mt-0.5`} />
                        <div className="min-w-0">
                          <p className={`text-[10px] font-black uppercase tracking-widest ${tpl.color}`}>{tpl.label}</p>
                          <p className="text-[9px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{tpl.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="px-4 py-2.5 border-t border-gray-100 dark:border-white/5">
                <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-600 text-center">
                  Select a template to customise
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Version History Panel ── */}
      <AnimatePresence>
        {versionHistoryOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setVersionHistoryOpen(false)}
              className="absolute inset-0 bg-black/20 dark:bg-black/40 z-30"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-[#040d1f] border-l border-gray-200 dark:border-white/8 z-40 flex flex-col"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-2">
                  <History className="w-3.5 h-3.5 text-violet-500" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-zinc-300">Version History</span>
                </div>
                <button
                  onClick={() => setVersionHistoryOpen(false)}
                  title="Close version history"
                  className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {savedVersions.length === 0 ? (
                  <div className="text-center py-16 px-4">
                    <Clock className="w-8 h-8 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">No saved versions yet</p>
                    <p className="text-[9px] text-slate-400 dark:text-slate-600 mt-1">Generated content will appear here.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-white/5">
                    {savedVersions.map(v => (
                      <div key={v.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors group">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-bold text-slate-700 dark:text-zinc-300 truncate">{v.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400">{v.mode}</span>
                              <span className="text-[8px] text-slate-400">{v.date.toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                            {v.blobUrl && (
                              <a href={v.blobUrl} download className="p-1 text-slate-400 hover:text-blue-500 transition-colors" title="Download">
                                <Download className="w-3 h-3" />
                              </a>
                            )}
                            <button
                              onClick={() => {
                                setQuery(v.content.slice(0, 100));
                                setVersionHistoryOpen(false);
                              }}
                              className="p-1 text-slate-400 hover:text-emerald-500 transition-colors"
                              title="Reuse"
                            >
                              <RefreshCw className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-4 py-2.5 border-t border-gray-100 dark:border-white/5">
                <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-600 text-center">
                  {savedVersions.length} version{savedVersions.length !== 1 ? 's' : ''} saved
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Resource Sharing Panel ── */}
      <AnimatePresence>
        {resourceSharingOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setResourceSharingOpen(false)}
              className="absolute inset-0 bg-black/20 dark:bg-black/40 z-30"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-[#040d1f] border-l border-gray-200 dark:border-white/8 z-40 flex flex-col"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-2">
                  <Share2 className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-zinc-300">Share & Export</span>
                </div>
                <button
                  onClick={() => setResourceSharingOpen(false)}
                  title="Close sharing panel"
                  className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">Export Formats</p>
                {[
                  { icon: FileText, label: 'Export as PDF', desc: 'Download the current conversation or generated content as a PDF', action: () => handleDownloadPDF(), color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/10', border: 'border-emerald-200 dark:border-emerald-500/20' },
                  { icon: ClipboardList, label: 'Export as Text', desc: 'Copy all messages as plain text to clipboard', action: () => { navigator.clipboard.writeText(messages.map(m => `${m.role}: ${m.content}`).join('\n\n')); }, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/10', border: 'border-blue-200 dark:border-blue-500/20' },
                ].map((exp, i) => {
                  const Icon = exp.icon;
                  return (
                    <button
                      key={i}
                      onClick={exp.action}
                      disabled={messages.length === 0}
                      className={`w-full text-left p-3 border ${exp.border} ${exp.bg} hover:opacity-80 transition-all disabled:opacity-30 disabled:pointer-events-none`}
                    >
                      <div className="flex items-start gap-2.5">
                        <Icon className={`w-4 h-4 ${exp.color} shrink-0 mt-0.5`} />
                        <div className="min-w-0">
                          <p className={`text-[10px] font-black uppercase tracking-widest ${exp.color}`}>{exp.label}</p>
                          <p className="text-[9px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{exp.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
                <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1 mt-4">Share Options</p>
                {[
                  { icon: Share2, label: 'Share with Colleague', desc: 'Generate a shareable link to this resource', color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/10', border: 'border-purple-200 dark:border-purple-500/20' },
                  { icon: BookMarked, label: 'Save to Resources', desc: 'Store in your teaching resources library', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/10', border: 'border-amber-200 dark:border-amber-500/20' },
                  { icon: ArrowUpRight, label: 'Export to Google Drive', desc: 'Send the PDF directly to your Google Drive', color: 'text-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-900/10', border: 'border-cyan-200 dark:border-cyan-500/20' },
                ].map((share, i) => {
                  const Icon = share.icon;
                  return (
                    <button
                      key={i}
                      className={`w-full text-left p-3 border ${share.border} ${share.bg} hover:opacity-80 transition-all`}
                    >
                      <div className="flex items-start gap-2.5">
                        <Icon className={`w-4 h-4 ${share.color} shrink-0 mt-0.5`} />
                        <div className="min-w-0">
                          <p className={`text-[10px] font-black uppercase tracking-widest ${share.color}`}>{share.label}</p>
                          <p className="text-[9px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{share.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="px-4 py-2.5 border-t border-gray-100 dark:border-white/5">
                <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-600 text-center">
                  Export or share your generated content
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IndependentSearchUtility;
